<template>
  <button
    v-bind="attrs"
    type="button"
    :aria-busy="isHandling || undefined"
    :disabled="isDisabled"
    @click="handleClick"
  >
    <slot />
  </button>
  <SeasonClosedDialog v-model:open="isDialogOpen" :season-state="closedState" />
</template>

<script setup lang="ts">
import { computed, ref, useAttrs } from "vue";
import { useRouter } from "vue-router";
import { useBlueberry, type BlueberrySeasonState } from "@acme/api";
import { orderRoute } from "@/router/navigation";
import SeasonClosedDialog from "@/components/SeasonClosedDialog.vue";
import { isOrderingUnavailable } from "@/lib/seasonGate";

defineOptions({ inheritAttrs: false });

const emit = defineEmits<{
  (e: "handled"): void;
}>();

const attrs = useAttrs();
const router = useRouter();
const blueberry = useBlueberry().getBlueberry();
const isDialogOpen = ref(false);
const isHandling = ref(false);

const orderingAvailable = computed(
  () => blueberry.value.success && blueberry.value.content.ordering_available,
);
const orderingUnavailable = computed(() =>
  isOrderingUnavailable(blueberry.value),
);
const hasDisabledAttr = computed(
  () => attrs.disabled !== undefined && attrs.disabled !== false,
);
const isDisabled = computed(() => hasDisabledAttr.value || isHandling.value);

const closedState = computed<Exclude<BlueberrySeasonState, "open">>(() => {
  if (
    blueberry.value.success &&
    blueberry.value.content.season_state === "after_end"
  ) {
    return "after_end";
  }
  return "before_start";
});

async function handleClick() {
  if (isHandling.value) return;

  isHandling.value = true;
  try {
    if (blueberry.value.loading) {
      await blueberry.refresh();
    }

    if (blueberry.value.failure) return;

    if (orderingAvailable.value) {
      await router.push(orderRoute);
      emit("handled");
      return;
    }

    isDialogOpen.value = orderingUnavailable.value;
  } finally {
    isHandling.value = false;
  }
}
</script>
